import React from 'react';
import {Box, Stack, Tooltip, Typography} from '@mui/material';
import {getPageImageUrl} from './sourcesApi';

// Recognized-object outline (idle state). Every detected block — header, text, image, table,
// footer, … — gets the SAME light-red dashed outline over a fully transparent fill so the page
// stays readable and the overlay never competes with the yellow cross-pane highlight below.
// The block's actual type is surfaced on hover via the tooltip instead of via colour coding.
const IDLE_STROKE = 'rgba(239, 68, 68, 0.55)';

// Cross-pane correspondence colours. Yellow is reserved exclusively for "this is the block
// matching what you're pointing at / selected in the YDOC pane".
//
// These MUST stay numerically identical to the YDOC text pane's own hover/highlight values
// (`--hover-bg` / `--highlight-bg` and `--hover-color` in ReadonlyProseMirrorRenderer) so that
// hovering a PDF object and hovering its YDOC segment look symmetrical. Hover uses a dashed
// amber outline, selection a solid one — same as the text pane.
const MATCH_FILL_SELECTED = 'rgba(255, 236, 60, 0.35)';
const MATCH_FILL_HOVER    = 'rgba(255, 236, 60, 0.18)';
const MATCH_STROKE        = 'rgba(220, 170, 0, 0.7)';

/**
 * Human-readable label for a MinerU layout type: `paragraph_title` → "Paragraph Title",
 * `aside_text` → "Aside Text", `header` → "Header".
 */
function formatLayoutType(rawType) {
    const t = String(rawType || '').trim();
    if (!t) return 'Block';
    return t
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Flattens a MinerU block's `content` into a plain-text preview.
 *
 * MinerU 4.0.0 emits `content` as an array of span objects
 * (`[{type: 'text', content: 'Hi'}, {type: 'equation_inline', content: '^{1}'}]`), and tables
 * nest an HTML string under a `table_body` span. Interpolating that array directly is what
 * produced "[object Object]" in the tooltip, so unwrap it recursively and stringify only leaves.
 */
function extractBlockText(content, depth = 0) {
    if (content == null || depth > 4) return '';
    if (typeof content === 'string') return content;
    if (typeof content === 'number' || typeof content === 'boolean') return String(content);
    if (Array.isArray(content)) {
        return content
            .map((span) => extractBlockText(span, depth + 1))
            .filter(Boolean)
            .join(' ');
    }
    if (typeof content === 'object') {
        // Skip binary-ish payloads that have no readable preview value.
        if (content.type === 'image_body') return '';
        return extractBlockText(content.content ?? content.text ?? '', depth + 1);
    }
    return '';
}

/** Collapses whitespace/newlines and truncates for a single-line tooltip preview. */
function previewText(raw, maxLen = 90) {
    const flat = String(raw || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!flat) return '';
    return flat.length > maxLen ? `${flat.slice(0, maxLen)}…` : flat;
}

/** Flatten per-page model_output into blocks with stable global indices.
 * Each block carries `blockId` (server-stamped `p{page}_b{local}`, the shared anchor key)
 * plus `page_idx`/`globalIdx` for legacy positional fallback. */
export function flattenModelOutput(modelOutputPages) {
    if (!Array.isArray(modelOutputPages)) return [];
    const flat = [];
    modelOutputPages.forEach((pageBlocks, pageIdx) => {
        if (!Array.isArray(pageBlocks)) return;
        pageBlocks.forEach((block) => {
            flat.push({
                ...block,
                page_idx: block?.pageIndex ?? pageIdx,
                globalIdx: flat.length,
            });
        });
    });
    return flat;
}

/** Resolves the sync key for a block: prefer the stable server id, fall back to globalIdx. */
function blockKey(block) {
    return block?.blockId ?? block?.globalIdx ?? null;
}

// Pages within this many pixels of the scroll container edge are pre-fetched so scrolling
// feels instant. Pages outside this margin only hold an aspect-ratio placeholder.
// PERF: Keep tight — 600px was causing all pages within a full-height panel to load at once
// on long documents. 400px ≈ half a typical panel height.
const PRELOAD_MARGIN_PX = 400;

const PdfPageWithOverlay = React.memo(function PdfPageWithOverlay({
    referenceId,
    userId,
    pageIndex,
    pageBlocks,
    globalIndexOffset,
    highlightedBlockKey,
    hoverBlockKey,
    onBlockClick,
    onBlockHover,
    forceVisible,
    // PERF: The scroll container ref from the parent SourcesPdfBboxViewer. Passing the actual
    // scrollable ancestor as `root` to IntersectionObserver ensures pages are evaluated against
    // the panel's scroll position rather than the viewport. Without this (root: null), all pages
    // physically within 400px of the viewport edge fire as "intersecting" even if the scroll
    // panel is half-height, causing too many pages to load simultaneously.
    scrollContainerRef,
}) {
    const containerRef = React.useRef(null);
    const [visible, setVisible] = React.useState(false);

    // Use useLayoutEffect so the observer is registered before the browser paints — the same
    // approach as PmDocContent's scrollRoot fix. With a plain useEffect, the first paint could
    // fire IO callbacks before the observer exists, missing the initial visibility check.
    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const scrollContainer = scrollContainerRef?.current ?? null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            {root: scrollContainer, rootMargin: `${PRELOAD_MARGIN_PX}px 0px`},
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [scrollContainerRef]);

    const imgSrc = (visible || forceVisible) ? getPageImageUrl({referenceId, userId, pageIndex}) : null;
    // Track the image's natural aspect ratio so the page box reserves the correct height
    // *before* the bitmap finishes decoding — this avoids the small→full-size snap that
    // happened when the container was sized from a JS-measured pixel width post-load.
    // Falls back to US-Letter (8.5×11) until the real ratio is known.
    const [aspectRatio, setAspectRatio] = React.useState('8.5 / 11');

    const onImgLoad = React.useCallback((e) => {
        const img = e.currentTarget;
        if (img?.naturalWidth && img.naturalHeight) {
            setAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
        }
    }, []);

    React.useEffect(() => {
        setAspectRatio('8.5 / 11');
    }, [imgSrc]);

    return (
        <Box
            ref={containerRef}
            data-page-index={pageIndex}
            sx={{
                position: 'relative',
                width: '100%',
                aspectRatio,
                bgcolor: 'white',
                mb: 2,
                boxShadow: 2,
            }}
        >
            {imgSrc && (
                <Box
                    component="img"
                    src={imgSrc}
                    alt={`Page ${pageIndex + 1}`}
                    onLoad={onImgLoad}
                    draggable={false}
                    sx={{display: 'block', width: '100%', height: '100%', objectFit: 'contain'}}
                />
            )}

            {pageBlocks.map((block, i) => {
                const globalIdx = globalIndexOffset + i;
                const key = block?.blockId ?? globalIdx;
                const type = String(block.type || '').toLowerCase();
                const isHighlighted = highlightedBlockKey != null
                    && (highlightedBlockKey === key || highlightedBlockKey === globalIdx);
                const isHovered = !isHighlighted && hoverBlockKey != null
                    && (hoverBlockKey === key || hoverBlockKey === globalIdx);
                const isMatched = isHighlighted || isHovered;
                // MinerU 4.0.0 `content` is a span array — flatten it, never interpolate raw
                // (that is what rendered "[object Object]").
                const blockText = previewText(
                    extractBlockText(block.content ?? block.text ?? ''),
                );
                const layoutTypeLabel = formatLayoutType(type);
                // model_output bbox is already a 0..1 fraction → percentages directly, no pixel
                // size needed. Overlays render immediately (no wait for image onLoad).
                const [bx0 = 0, by0 = 0, bx1 = 0, by1 = 0] = block.bbox ?? [];
                const leftPct = bx0 * 100;
                const topPct = by0 * 100;
                const widthPct = Math.max(0.1, (bx1 - bx0) * 100);
                const heightPct = Math.max(0.1, (by1 - by0) * 100);

                return (
                    <Tooltip
                        key={globalIdx}
                        title={
                            <span style={{fontSize: 11}}>
                                <b>Layout Type: {layoutTypeLabel}</b>
                                {blockText ? <><br/>{blockText}</> : null}
                            </span>
                        }
                        placement="top"
                        arrow
                    >
                        <Box
                            // Addressable so scrollToBlock can centre the *block* rather than the
                            // top of its page (see scrollBlockIntoView in the parent).
                            data-block-key={String(block?.blockId ?? globalIdx)}
                            onClick={() => onBlockClick?.(block?.blockId ?? globalIdx, pageIndex)}
                            onMouseEnter={() => onBlockHover?.(block?.blockId ?? globalIdx, pageIndex)}
                            onMouseLeave={() => onBlockHover?.(null, pageIndex)}
                            sx={{
                                position: 'absolute',
                                left: `${leftPct}%`,
                                top: `${topPct}%`,
                                width: `${widthPct}%`,
                                height: `${heightPct}%`,
                                // Idle: transparent fill + light-red dashed outline (recognized object).
                                // Matched: same yellow the YDOC pane uses — solid border when the
                                // block is selected, dashed while merely hovered (mirrors the text
                                // pane's outline treatment so both sides read identically).
                                bgcolor: isHighlighted
                                    ? MATCH_FILL_SELECTED
                                    : isHovered ? MATCH_FILL_HOVER : 'transparent',
                                border: isHighlighted
                                    ? `2px solid ${MATCH_STROKE}`
                                    : isHovered
                                        ? `2px dashed ${MATCH_STROKE}`
                                        : `1.5px dashed ${IDLE_STROKE}`,
                                borderRadius: '2px',
                                cursor: 'pointer',
                                transition: 'background-color 0.15s, border-color 0.15s',
                                // Direct mouse-over on the PDF side must look IDENTICAL to hovering
                                // the corresponding segment in the YDOC pane, so the correspondence
                                // reads the same in both directions. (onMouseEnter already sets
                                // pdfHoverKey → isHovered; this rule only covers the frame before
                                // that state lands, or when no hover handler is wired.)
                                '&:hover': isMatched ? undefined : {
                                    bgcolor: MATCH_FILL_HOVER,
                                    border: `2px dashed ${MATCH_STROKE}`,
                                },
                            }}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );
});

/**
 * Renders the original PDF (page PNGs) with MinerU model_output bbox overlays.
 *
 * @param {number}   referenceId
 * @param {string}   userId
 * @param {Array[]}  modelOutputPages - per-page block arrays from layout API
 * @param {string|number} highlightedBlockKey - shared blockId (or legacy globalIdx) to highlight
 * @param {function} onBlockClick - called with the clicked block's blockId (or legacy globalIdx)
 */
const SourcesPdfBboxViewer = React.forwardRef(function SourcesPdfBboxViewer(
    {referenceId, userId, modelOutputPages, highlightedBlockKey, hoverBlockKey, onBlockClick, onBlockHover},
    ref,
) {
    const scrollRef = React.useRef(null);
    const [forcedVisiblePage, setForcedVisiblePage] = React.useState(null);
    const flatBlocks = React.useMemo(
        () => flattenModelOutput(modelOutputPages),
        [modelOutputPages],
    );

    const pageCount = Array.isArray(modelOutputPages) ? modelOutputPages.length : 0;

    // Resolve a block by its shared key (stable blockId or legacy globalIdx).
    const findBlock = React.useCallback((key) => {
        if (key == null || !flatBlocks.length) return null;
        return flatBlocks.find((b) => blockKey(b) === key || b.globalIdx === key) ?? null;
    }, [flatBlocks]);

    // Global index offset per page for overlay click indices
    const pageOffsets = React.useMemo(() => {
        const offsets = [];
        let sum = 0;
        for (let i = 0; i < pageCount; i++) {
            offsets.push(sum);
            const blocks = modelOutputPages[i];
            sum += Array.isArray(blocks) ? blocks.length : 0;
        }
        return offsets;
    }, [modelOutputPages, pageCount]);

    // ── Precise cross-pane scrolling ─────────────────────────────────────────
    // Scrolling to `[data-page-index]` only lands on the TOP of the page, so clicking a passage
    // near the bottom of a page left the reader looking at the wrong part of it. Instead centre
    // the block itself: prefer the rendered overlay element's real geometry, and fall back to
    // computing the offset from the block's normalized bbox (bbox[1] is a 0..1 page fraction).
    //
    // We adjust `scrollTop` on our own container rather than calling `scrollIntoView`, because
    // scrollIntoView also scrolls every scrollable ancestor and visibly jolts the split-pane shell.
    const scrollBlockIntoView = React.useCallback((key, pageIdx, bbox) => {
        const container = scrollRef.current;
        if (!container) return;
        const pageEl = container.querySelector(`[data-page-index="${pageIdx}"]`);
        if (!pageEl) return;

        const containerRect = container.getBoundingClientRect();
        let targetTop;
        let targetHeight;

        const overlayEl = key != null
            ? pageEl.querySelector(`[data-block-key="${CSS.escape(String(key))}"]`)
            : null;

        if (overlayEl) {
            const r = overlayEl.getBoundingClientRect();
            targetTop = r.top - containerRect.top + container.scrollTop;
            targetHeight = r.height;
        } else if (Array.isArray(bbox) && bbox.length >= 4) {
            // No overlay node (or key belongs to a different id space) — derive from the bbox.
            const pageRect = pageEl.getBoundingClientRect();
            const pageTop = pageRect.top - containerRect.top + container.scrollTop;
            targetTop = pageTop + (Number(bbox[1]) || 0) * pageRect.height;
            targetHeight = Math.max(0, ((Number(bbox[3]) || 0) - (Number(bbox[1]) || 0)) * pageRect.height);
        } else {
            const pageRect = pageEl.getBoundingClientRect();
            targetTop = pageRect.top - containerRect.top + container.scrollTop;
            targetHeight = 0;
        }

        // Centre the block, but never scroll past the top of the document.
        const desired = targetTop - Math.max(0, (container.clientHeight - targetHeight) / 2);
        container.scrollTo({
            top: Math.max(0, desired),
            behavior: 'smooth',
        });
    }, []);

    // The page bitmap decides the page element's real height. Until it loads we only have the
    // 8.5/11 placeholder ratio, so a scroll issued right after forcing a page visible can land
    // slightly off. Remember the pending target and re-centre once layout settles.
    const pendingScrollRef = React.useRef(null);

    const requestBlockScroll = React.useCallback((key, pageIdx, bbox) => {
        if (pageIdx == null) return;
        pendingScrollRef.current = {key, pageIdx, bbox};
        setForcedVisiblePage(pageIdx);
        requestAnimationFrame(() => scrollBlockIntoView(key, pageIdx, bbox));
    }, [scrollBlockIntoView]);

    // Re-centre after images/layout settle so the first (placeholder-height) scroll is corrected.
    React.useEffect(() => {
        const pending = pendingScrollRef.current;
        if (!pending) return;
        const timers = [
            setTimeout(() => scrollBlockIntoView(pending.key, pending.pageIdx, pending.bbox), 120),
            setTimeout(() => {
                scrollBlockIntoView(pending.key, pending.pageIdx, pending.bbox);
                pendingScrollRef.current = null;
            }, 450),
        ];
        return () => timers.forEach(clearTimeout);
    }, [forcedVisiblePage, scrollBlockIntoView]);

    React.useImperativeHandle(ref, () => ({
        // pageIndex is a fallback: for content_list–derived PM docs the blockKey won't
        // match any model_output block (different ID space), but attrs.pageIndex on the
        // PM node gives us the exact page to scroll to directly.
        scrollToBlock(key, pageIndex, bbox) {
            const block = findBlock(key);
            const pageIdx = block ? (block.page_idx ?? 0) : pageIndex;
            requestBlockScroll(key, pageIdx, block?.bbox ?? bbox);
        },
    }));

    React.useEffect(() => {
        if (highlightedBlockKey == null || !scrollRef.current) return;
        const block = findBlock(highlightedBlockKey);
        if (!block) return;
        requestBlockScroll(highlightedBlockKey, block.page_idx ?? 0, block.bbox);
    }, [highlightedBlockKey, findBlock, requestBlockScroll]);

    if (!referenceId || !userId) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" p={3}>
                <Typography variant="body2" color="text.secondary">
                    PDF viewer unavailable.
                </Typography>
            </Stack>
        );
    }

    if (!pageCount) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" p={3} spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                    Layout blocks not available yet.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Reprocess the document to generate layout data.
                </Typography>
            </Stack>
        );
    }

    return (
        <Box
            ref={scrollRef}
            sx={{
                flex: 1,
                overflowY: 'auto',
                bgcolor: '#525659',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {Array.from({length: pageCount}, (_, pageIndex) => (
                <PdfPageWithOverlay
                    key={pageIndex}
                    referenceId={referenceId}
                    userId={userId}
                    pageIndex={pageIndex}
                    pageBlocks={modelOutputPages[pageIndex] ?? []}
                    globalIndexOffset={pageOffsets[pageIndex] ?? 0}
                    highlightedBlockKey={highlightedBlockKey}
                    hoverBlockKey={hoverBlockKey}
                    onBlockClick={onBlockClick}
                    onBlockHover={onBlockHover}
                    forceVisible={forcedVisiblePage === pageIndex}
                    scrollContainerRef={scrollRef}
                />
            ))}
            <Typography variant="caption" sx={{color: 'grey.400', mt: 1, mb: 1}}>
                {pageCount} page{pageCount !== 1 ? 's' : ''}
                {flatBlocks.length ? ` · ${flatBlocks.length} layout blocks` : ''}
            </Typography>
        </Box>
    );
});

export default SourcesPdfBboxViewer;
